import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { EXIST_PATH } from '../../modules/API/settings'
import EditIcon from '../icons/IconLib/PencilIcon'
import ScalesIcon from '../icons/IconLib/ScaleIcon'
import { BasketProduct } from './types/basketTypes'

interface PackTypeStyle {
  color: string
  component: ReactNode
}

interface Props {
  product: BasketProduct
  packTypeStyle: PackTypeStyle
  pricePerUnit: number
  totalProductPrice: number
  innerWeight: number
  weightPerPack: number
  isExpanded: boolean
  onToggle: () => void
}

const BasketItem: React.FC<Props> = ({
  product,
  packTypeStyle,
  pricePerUnit,
  totalProductPrice,
  innerWeight,
  weightPerPack,
  isExpanded,
  onToggle,
}) => {
  const hasRemarks = !!(product.Remarks?.trim() || product.Remark?.trim())
  const imageSrc = product.ImageUrl ? `${EXIST_PATH}${product.ImageUrl}` : '/emptyBox.png'
  const productName = `${product.ProductDesc || product.desc || ''} ${product.VarietyDesc ?? ''} ${product.SizeID ?? ''}`.trim()

  return (
    <Card
      $hasRemarks={hasRemarks}
      $expanded={isExpanded}
      onClick={onToggle}
      role='button'
      tabIndex={0}
    >
      <MainRow>
        <Body>
          <Name>{productName}</Name>
          <QtyBadge aria-label='quantity'>
            <PackTypeIcon $color={packTypeStyle.color} aria-hidden>
              {packTypeStyle.component}
            </PackTypeIcon>
            <Mono>{product.Packs}</Mono>
          </QtyBadge>
        </Body>

        <Trailing>
          <Thumb src={imageSrc} alt={product.ProductDesc || product.desc || ''} />
        </Trailing>
      </MainRow>

      <ExpandRow $visible={isExpanded}>
        <DetailRow>
          <DetailSide>
            {/* <DetailLabel>ליחידה</DetailLabel> */}
            <Mono>{pricePerUnit.toFixed(2)}</Mono>
            <Sym>₪</Sym>
          </DetailSide>
          <DetailSide>
            <DetailLabel>סה"כ</DetailLabel>
            <Mono>{totalProductPrice.toFixed(2)}</Mono>
            <Sym>₪</Sym>
          </DetailSide>
        </DetailRow>

        <DetailRow>
          <DetailSide>
            {/* <DetailLabel>ליחידה</DetailLabel> */}
            <Mono>{weightPerPack.toFixed(2)}</Mono>
            <DetailIcon><ScalesIcon /></DetailIcon>
          </DetailSide>
          <DetailSide>
            <DetailLabel>סה"כ</DetailLabel>
            <Mono>{innerWeight.toFixed(2)}</Mono>
            <DetailIcon><ScalesIcon /></DetailIcon>
          </DetailSide>
        </DetailRow>

        {hasRemarks && (
          <RemarkRow>
            <EditIcon />
            <span>{product.Remarks || product.Remark}</span>
          </RemarkRow>
        )}
      </ExpandRow>
    </Card>
  )
}

export default BasketItem

const Card = styled.div<{ $hasRemarks: boolean; $expanded: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.625rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  cursor: pointer;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  ${({ $hasRemarks, theme }) =>
    $hasRemarks &&
    `border-inline-start: 3px solid ${theme.colors.danger};`}

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }
`

const MainRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
`

const PackTypeIcon = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  color: ${({ $color }) => $color || 'currentColor'};
  svg { width: 0.875rem; height: 0.875rem; }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  text-align: start;
`

const Name = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`

const QtyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.ink};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-size: ${({ theme }) => theme.text.xs};
  line-height: 1;
`

const Trailing = styled.div`
  display: inline-flex;
  align-items: center;
`

const Thumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.radii.sm};
  flex-shrink: 0;
`

const ExpandRow = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  direction: rtl;
`

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const DetailSide = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  color: ${({ theme }) => theme.colors.ink};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.md};
`

const DetailLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ theme }) => theme.colors.ink3};
  font-weight: 500;
`

const DetailIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.icon};
  svg { width: 0.75rem; height: 0.75rem; }
`

const Mono = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
`

const Sym = styled.span`
  color: ${({ theme }) => theme.colors.ink3};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
`

const RemarkRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  color: ${({ theme }) => theme.colors.ink2};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  line-height: 1.4;
  text-align: start;

  svg { width: 0.75rem; height: 0.75rem; color: ${({ theme }) => theme.colors.icon}; margin-top: 0.2rem; }
`
